import React, { Component } from 'react';
import { Row, Col, Modal, ModalBody, ModalHeader, Input, Form, Card, CardBody, Button, CustomInput, Badge } from 'reactstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getMarkupType, priceMarkupIncludePCOptions } from '../Base/Common/ReferenceDataFunctions';
import { handleNotification } from '../Base/Notification';
import { ComboBoxWithTitle } from './ComboBoxWithTitle';
import CustomSelect from '../Base/Common/CustomSelect';
import CustomToolTip from '../../Utils/CustomToolTip';
import { ErrorAlert } from '../Common/ErrorAlert';
import { postAPI } from '../Base/API';
import BlockUi from 'react-block-ui';
import InfiniteScroll from 'react-infinite-scroller';
import { suggestMappings, resolveInterfaceMode } from './MappingSuggestions';

// Initial number of suggestion rows to mount; more are appended as the user scrolls. Each row
// carries a react-select, so painting all 800+ at once froze the modal — capping the first paint
// is what keeps opening responsive.
const ROW_BATCH = 20;

const tierColor = { high: 'success', medium: 'warning', low: 'secondary' };

class MappingSuggestionsModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: null,
            block: false,
            // Suggestions are computed in componentDidMount, not here: suggestMappings is a
            // synchronous, CPU-heavy pass over every local rate x every channel rate. Running it
            // in the constructor would block the main thread before the modal can paint, so the
            // user sees a frozen UI for seconds with no feedback. `computing` drives a spinner
            // until the deferred computation lands.
            computing: true,
            suggestions: [],
            selection: {},
            applyMarkup: false,
            visibleCount: ROW_BATCH,
            mappingData: {
                active: true,
                interfaceMode: null,
                priceMode: null,
                priceMarkupType: null,
                priceMarkupValue: null,
                priceMarkupIncludePackageComponents: null
            },
            progress: null
        };
    }

    // Append the next batch of rows when the user scrolls near the bottom of the list.
    loadMore = () => {
        this.setState(prev => ({
            visibleCount: Math.min(prev.visibleCount + ROW_BATCH, prev.suggestions.length)
        }));
    }

    componentDidMount() {
        // Defer the heavy matching until AFTER the browser has painted the spinner. A single
        // setTimeout(0) can still run before paint; a double requestAnimationFrame fires after a
        // full frame is committed, so the "computing" spinner is guaranteed to be on screen
        // before the main thread blocks on suggestMappings.
        this._raf1 = requestAnimationFrame(() => {
            this._raf2 = requestAnimationFrame(() => {
                // Build deterministic suggestions once; keep only rates that have at least one candidate.
                const suggestions = suggestMappings(this.props.rateList, this.props.channelDataMap)
                    .filter(s => s.suggestions.length > 0);

                // Default selection: pre-pick the top candidate, and pre-accept it when it is not low confidence.
                const selection = {};
                suggestions.forEach(s => {
                    const top = s.suggestions[0];
                    selection[s.rateId] = { channelId: top.channelId, accepted: top.tier !== 'low' };
                });

                this.setState({ suggestions, selection, computing: false });
            });
        });
    }

    componentWillUnmount() {
        if (this._raf1) cancelAnimationFrame(this._raf1);
        if (this._raf2) cancelAnimationFrame(this._raf2);
    }

    handleChangeSelect = (name, combo) => {
        this.setState(prevState => ({
            mappingData: { ...prevState.mappingData, [name]: combo ? combo.value : null }
        }));
    }

    handleChange = (evt) => {
        const { name, value } = evt.target;
        this.setState(prevState => ({ mappingData: { ...prevState.mappingData, [name]: value } }));
    }

    changeApplyMarkup = (evt) => {
        const checked = evt.target.checked;
        this.setState(prevState => ({
            applyMarkup: checked,
            mappingData: {
                ...prevState.mappingData,
                priceMarkupType: checked ? prevState.mappingData.priceMarkupType : null,
                priceMarkupValue: checked ? prevState.mappingData.priceMarkupValue : null
            }
        }));
    }

    toggleAccepted = (rateId) => {
        this.setState(prevState => {
            const current = prevState.selection[rateId] || {};
            return {
                selection: {
                    ...prevState.selection,
                    [rateId]: { ...current, accepted: !current.accepted }
                }
            };
        });
    }

    changeCandidate = (rateId, combo) => {
        this.setState(prevState => {
            const current = prevState.selection[rateId] || {};
            return {
                selection: {
                    ...prevState.selection,
                    [rateId]: { ...current, channelId: combo ? combo.value : null }
                }
            };
        });
    }

    // Mirrors EditChannelMapping's pricePosition logic for the simple-channel save path.
    getPricePosition = (occupancyList, priceMode) => {
        if (priceMode !== 0 || !occupancyList || occupancyList.length === 0) return 0;
        const maxOccupancy = occupancyList[occupancyList.length - 1];
        const maxAdults = 8;
        return maxOccupancy.childrens > 0 ? maxAdults + maxOccupancy.childrens : maxOccupancy.adults;
    }

    // Keep the user's preferred interface mode when valid for this rate, otherwise fall back
    // to a valid one (dependent / AvailabilityOnly rates default to Reservation/Inventory).
    resolvedMode = (channelData) => resolveInterfaceMode(channelData, this.state.mappingData.interfaceMode);

    buildRequest = (suggestion, channelData) => {
        const { mappingData, applyMarkup } = this.state;
        return {
            request: {
                id: null,
                rateCodeId: suggestion.rateId,
                active: mappingData.active,
                channelInstanceId: this.props.selectedChannel.id,
                priceMode: mappingData.priceMode !== null ? mappingData.priceMode : -1,
                priceMarkupType: applyMarkup && mappingData.priceMarkupType ? mappingData.priceMarkupType : 0,
                priceMarkupValue: applyMarkup && mappingData.priceMarkupValue ? mappingData.priceMarkupValue : 0,
                priceMarkupIncludePackageComponents: applyMarkup ? mappingData.priceMarkupIncludePackageComponents : null,
                interfaceMode: this.resolvedMode(channelData) || -1,
                iCalUrl: null,
                pricePosition: this.getPricePosition(suggestion.occupancyList, mappingData.priceMode),
                associatedRateCodeChannels: [],
                excludePriceComponent: false,
                occupationsToSend: [],
                rateCodeMapping: channelData.rateCode,
                roomTypeMapping: channelData.roomCode
            }
        };
    }

    // Promise wrapper around the callback-based postAPI so we can save sequentially.
    saveOne = (req) => new Promise(resolve => {
        postAPI(result => {
            const { data, error } = result;
            if (error) return resolve({ ok: false });
            if (data && (!data.errors || data.errors.length === 0)) return resolve({ ok: true });
            return resolve({ ok: false, data });
        }, '/api/Rate/RateCodeChannel/v1/ratecodechannel', req);
    });

    confirm = async (e) => {
        e.preventDefault();
        const { suggestions, selection } = this.state;

        const toCreate = suggestions
            .map(s => {
                const sel = selection[s.rateId];
                if (!sel || !sel.accepted || !sel.channelId) return null;
                const cd = this.props.channelDataMap.find(c => c.id === sel.channelId);
                return cd ? { suggestion: s, channelData: cd } : null;
            })
            .filter(Boolean);

        if (toCreate.length === 0) {
            handleNotification({ warnings: [{ code: '', message: <FormattedMessage id="MappingSuggestions.NothingSelected" /> }] });
            return;
        }

        this.setState({ block: true, progress: { done: 0, total: toCreate.length, failed: 0 } });

        let failed = 0;
        for (let i = 0; i < toCreate.length; i++) {
            const { suggestion, channelData } = toCreate[i];
            // eslint-disable-next-line no-await-in-loop
            const res = await this.saveOne(this.buildRequest(suggestion, channelData));
            if (!res.ok) failed++;
            this.setState({ progress: { done: i + 1, total: toCreate.length, failed } });
        }

        const created = toCreate.length - failed;
        const summary = <FormattedMessage id="MappingSuggestions.CreatedSummary" values={{ created, failed }} />;
        if (created === 0) {
            // Every save failed — surface as an error, not a success toast.
            handleNotification({ errors: [{ code: '', message: summary }] });
        } else if (failed > 0) {
            // Partial success — warn so the failures are not hidden behind a success toast.
            handleNotification({ warnings: [{ code: '', message: summary }] });
        } else {
            handleNotification({}, summary, <FormattedMessage id="EditChannelMapping.Success" />);
        }

        this.setState({ block: false });

        // Only refresh and close when something was actually created; on a total failure keep the
        // modal open so the user can retry without losing their selections.
        if (created > 0) {
            this.props.getRates();
            this.props.reloadChannelData();
            this.props.toggle();
        }
    }

    candidateOptions = (suggestion) => suggestion.suggestions.map(c => ({
        value: c.channelId,
        label: `(${c.channelRoomCode}/${c.channelRateCode}) ${c.channelRoomDescription || ''} - ${c.channelRateDescription || ''}`,
        score: c.score,
        tier: c.tier
    }));

    // When the preferred interface mode is not valid for the chosen rate (dependent /
    // AvailabilityOnly), show which mode will actually be applied instead.
    renderModeNote = (channelId) => {
        const preferred = this.state.mappingData.interfaceMode;
        if (!preferred || !channelId) return null;
        const cd = this.props.channelDataMap.find(c => c.id === channelId);
        const resolved = resolveInterfaceMode(cd, preferred);
        if (resolved === preferred) return null;
        const option = (this.props.interfaceModes || []).find(o => o.value === resolved);
        return (
            <div className="text-warning mt-1" style={{ fontSize: '11px' }}>
                <i className="fas fa-exclamation-triangle mr-1" />
                <FormattedMessage id="MappingSuggestions.ModeAdjusted" />{' '}
                {option ? option.label : resolved}
            </div>
        );
    }

    renderReasons = (suggestion, channelId) => {
        const c = suggestion.suggestions.find(s => s.channelId === channelId);
        if (!c || !c.reasons || c.reasons.length === 0) return '';
        const id = `reasons_${suggestion.rateId}`;
        return (
            <span>
                <Badge color={tierColor[c.tier]} className="text-white" id={id}>
                    {Math.round(c.score * 100)}%
                </Badge>
                <CustomToolTip placement="left" target={id}>
                    {c.reasons.map((r, k) => (
                        <Row key={k}><Col className="text-left"><FormattedMessage id={`MappingSuggestions.reason.${r.type}`} /></Col></Row>
                    ))}
                </CustomToolTip>
            </span>
        );
    }

    render() {
        const { interfaceModes, priceModes } = this.props;
        const { suggestions, selection, applyMarkup, mappingData, progress, block, computing, visibleCount } = this.state;
        const markupType = getMarkupType();

        const statusOptions = [
            { value: true, label: this.props.intl.formatMessage({ id: 'generic.active' }) },
            { value: false, label: this.props.intl.formatMessage({ id: 'generic.inactive' }) }
        ];

        const acceptedCount = Object.values(selection).filter(s => s.accepted && s.channelId).length;

        return (
            <Modal isOpen={this.props.modal} toggle={this.props.toggle} size="lg" fade={false}>
                <ModalHeader toggle={this.props.toggle}>
                    <FormattedMessage id="MappingSuggestions.Title" />
                </ModalHeader>
                <ModalBody className="p-0">
                    {computing ?
                        <div className="text-center text-muted py-5">
                            <i className="fas fa-circle-notch fa-spin fa-2x mb-3" />
                            <div><FormattedMessage id="MappingSuggestions.Computing" /></div>
                        </div>
                    :
                    <BlockUi tag="div" blocking={block}>
                        <ErrorAlert error={this.state.error} />
                        <Form onSubmit={this.confirm}>
                            <Card className="shadow border-0 p-0">
                                <CardBody className="m-0">
                                    <Row className="mb-2 align-items-center">
                                        <Col>
                                            <h5 className="m-0"><FormattedMessage id="MappingSuggestions.SharedConfig" /></h5>
                                        </Col>
                                        <Col className="text-right col-3">
                                            <Button className="btn-sm btn-host" type="submit" disabled={acceptedCount === 0}>
                                                <i className="fas fa-save mr-1" />
                                                {acceptedCount > 0 ? acceptedCount : ''}
                                            </Button>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="mb-5 col-4">
                                            <h5 className="mb-3"><FormattedMessage id="ChannelList.status" /></h5>
                                            <CustomSelect
                                                name="active" options={statusOptions}
                                                value={statusOptions.find(o => o.value === mappingData.active)}
                                                onChange={this.handleChangeSelect.bind(this, 'active')}
                                            />
                                        </Col>
                                        <ComboBoxWithTitle
                                            title={<FormattedMessage id="MappingSuggestions.PreferredInterface" />}
                                            name="interfaceMode" options={interfaceModes}
                                            onChangeFunc={this.handleChangeSelect.bind(this, 'interfaceMode')}
                                            required
                                        />
                                        <ComboBoxWithTitle
                                            title={<FormattedMessage id="EditChannelMapping.PriceMode" />}
                                            name="priceMode" options={priceModes}
                                            onChangeFunc={this.handleChangeSelect.bind(this, 'priceMode')}
                                            required
                                        />
                                    </Row>

                                    <Row className="mb-3 align-items-center">
                                        <Col className="col-4">
                                            <CustomInput type="switch" id="applyMarkup"
                                                checked={applyMarkup} onChange={this.changeApplyMarkup}
                                                label={this.props.intl.formatMessage({ id: 'EditChannelMapping.ApplyMarkup' })} />
                                        </Col>
                                        {applyMarkup &&
                                            <>
                                                <Col className="col-3">
                                                    <CustomSelect name="priceMarkupType" isClearable options={markupType}
                                                        placeholder={<FormattedMessage id="BulkOperation.Type" />}
                                                        onChange={this.handleChangeSelect.bind(this, 'priceMarkupType')}
                                                        required={applyMarkup} />
                                                </Col>
                                                <Col className="col-2">
                                                    <Input type="number" name="priceMarkupValue"
                                                        placeholder={this.props.intl.formatMessage({ id: 'BulkOperation.Value' })}
                                                        min={mappingData.priceMarkupType === 2 ? '-100.00' : '0.01'} max={mappingData.priceMarkupType === 2 ? undefined : '100.00'} step="0.01"
                                                        value={mappingData.priceMarkupValue || ''}
                                                        onChange={this.handleChange} required={applyMarkup} />
                                                </Col>
                                                <Col className="col-3">
                                                    <CustomSelect name="priceMarkupIncludePackageComponents"
                                                        options={priceMarkupIncludePCOptions}
                                                        placeholder={<FormattedMessage id="EditChannelMapping.Select" />}
                                                        onChange={this.handleChangeSelect.bind(this, 'priceMarkupIncludePackageComponents')}
                                                        required={applyMarkup} />
                                                </Col>
                                            </>
                                        }
                                    </Row>

                                    <h5 className="mb-2 mt-3">
                                        <FormattedMessage id="MappingSuggestions.Suggestions" /> ({suggestions.length})
                                        {suggestions.length > visibleCount &&
                                            <small className="text-muted ml-2">
                                                (<FormattedMessage id="MappingSuggestions.Showing"
                                                    values={{ shown: visibleCount, total: suggestions.length }} />)
                                            </small>
                                        }
                                    </h5>

                                    <div style={{ maxHeight: '45vh', overflow: 'auto' }}>
                                        <InfiniteScroll
                                            pageStart={0}
                                            initialLoad={false}
                                            loadMore={this.loadMore}
                                            hasMore={visibleCount < suggestions.length}
                                            useWindow={false}
                                            threshold={300}
                                        >
                                            {suggestions.slice(0, visibleCount).map((s, k) => {
                                                const sel = selection[s.rateId] || {};
                                                const options = this.candidateOptions(s); // build once per row, not twice
                                                return (
                                                    <Row key={s.rateId} className="overbglight align-items-center py-2" style={{ borderBottom: '1px solid #dee2e6' }}>
                                                        <Col className="col-1 text-center">
                                                            <CustomInput type="checkbox" id={`accept_${s.rateId}`}
                                                                checked={!!sel.accepted}
                                                                onChange={() => this.toggleAccepted(s.rateId)} />
                                                        </Col>
                                                        <Col className="col-4">
                                                            <div><b>{s.roomCode}</b> · {s.rateCode}</div>
                                                            <div className="text-muted" style={{ fontSize: '12px' }}>{s.rateDescription}</div>
                                                        </Col>
                                                        <Col className="col-6">
                                                            <CustomSelect
                                                                options={options}
                                                                value={options.find(o => o.value === sel.channelId)}
                                                                onChange={(combo) => this.changeCandidate(s.rateId, combo)}
                                                                isClearable={false}
                                                            />
                                                            {this.renderModeNote(sel.channelId)}
                                                        </Col>
                                                        <Col className="col-1 text-center px-0">
                                                            {this.renderReasons(s, sel.channelId)}
                                                        </Col>
                                                    </Row>
                                                );
                                            })}
                                        </InfiniteScroll>
                                        {suggestions.length === 0 &&
                                            <div className="text-muted text-center py-4">
                                                <FormattedMessage id="MappingSuggestions.NoSuggestions" />
                                            </div>
                                        }
                                    </div>

                                    {progress &&
                                        <div className="mt-2 small text-muted">
                                            <FormattedMessage id="MappingSuggestions.Progress"
                                                values={{ done: progress.done, total: progress.total, failed: progress.failed }} />
                                        </div>
                                    }
                                </CardBody>
                            </Card>
                        </Form>
                    </BlockUi>
                    }
                </ModalBody>
            </Modal>
        );
    }
}

export default injectIntl(MappingSuggestionsModal);
